// exportMixin wires the Export button + Processing/Error modal flow into a V4
// list page. Pages opt-in by including the mixin and supplying `exportFn`
// (a function returning a Promise that resolves to an axios response whose
// .data is a Blob).
//
// State surface:
//   - exporting (bool) — true while the request is in-flight (Processing modal)
//   - exportError (object|null) — { title, detail } when the server rejected
//     the request; pages render an error modal bound to it. Click Close →
//     `clearExportError()`.
//
// Filename: read from the response's Content-Disposition header so the
// timestamp the server stamped (yyyy-mm-dd_hh-mm-ss) survives the download.
// Pages may still set `exportFilename` as a fallback.

// The four helpers below are exported by name as well as being used by the
// mixin. A page that already spends its single `exportFn` on one download and
// needs a second one of a different content type (EasMigration.vue: the .xlsx
// export AND the voicemail backup .zip) reuses them instead of hand-rolling a
// weaker copy that forgets Content-Disposition or the JSON-error-as-Blob trap.
// The mixin's own behaviour is unchanged.
export function extractFilename(resp, fallback) {
  const cd = resp?.headers?.["content-disposition"] || "";
  // matches: attachment; filename="foo.xlsx" or filename=foo.xlsx
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (m?.[1]) {
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }
  return fallback || "export.xlsx";
}

// readBlobAsText handles the case where the server returned a JSON error but
// axios delivered the body as a Blob (because responseType: blob). We turn it
// back into JSON so the user sees Msg + Cause.
export function readBlobAsJSON(blob) {
  return new Promise(resolve => {
    if (!blob || !(blob instanceof Blob)) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(JSON.parse(reader.result));
      } catch {
        resolve({ Msg: reader.result });
      }
    };
    reader.onerror = () => resolve(null);
    reader.readAsText(blob);
  });
}

// Defensive: the server signalled an error with a JSON body but the status was
// still 200, so axios did not throw and the JSON arrived as a Blob.
export function isJsonBody(resp) {
  return Boolean(resp?.data?.type?.indexOf("application/json") === 0);
}

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Saves a streamed response as a file. The MIME type is a parameter because
// the same flow serves an .xlsx export and a .zip backup; everything else —
// object URL, the temporary anchor, revoking the URL — is identical, and the
// filename still comes from Content-Disposition so the timestamp the server
// stamped survives the download.
export function downloadBlob(resp, fallbackName, mime) {
  const blob = new Blob([resp.data], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = extractFilename(resp, fallbackName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function downloadSpreadsheet(resp, fallbackName) {
  downloadBlob(resp, fallbackName || "export.xlsx", XLSX_MIME);
}

// Title rules differ subtly between the two payload shapes; both are kept
// verbatim. Blob shape: an empty translation falls back to the raw key.
function titleFromBlobMsg(msg, t, fallback) {
  if (!msg) return fallback;
  const translated = t(msg);
  return translated && translated !== msg ? translated : msg;
}

// Plain-object shape: whatever $t returns is used as long as it differs from
// the key — including an empty string.
function titleFromDataMsg(msg, t, fallback) {
  if (!msg) return fallback;
  const translated = t(msg);
  return translated !== msg ? translated : msg;
}

async function describeBlobError(err, t, fallback) {
  const parsed = await readBlobAsJSON(err.response.data);
  if (!parsed) {
    return { title: fallback, detail: err.message || "" };
  }
  return {
    title: titleFromBlobMsg(parsed.Msg || "", t, fallback),
    detail: parsed.Cause || ""
  };
}

function describeDataError(err, t, fallback) {
  const d = err.response.data;
  return {
    title: titleFromDataMsg(d.Msg || "", t, fallback),
    detail: d.Cause || ""
  };
}

// Resolves to the { title, detail } pair rendered by the error modal.
export async function describeExportError(err, t, fallback) {
  if (err?.response?.data instanceof Blob) {
    // Axios error with blob payload — read it as text → JSON.
    return describeBlobError(err, t, fallback);
  }
  if (err?.response?.data) {
    return describeDataError(err, t, fallback);
  }
  if (err?.message) {
    return { title: fallback, detail: err.message };
  }
  return { title: fallback, detail: "" };
}

export default {
  data() {
    return {
      exporting: false,
      exportError: null
    };
  },
  methods: {
    clearExportError() {
      this.exportError = null;
    },
    async runExport() {
      if (typeof this.exportFn !== "function") return;
      this.exporting = true;
      this.exportError = null;
      try {
        const resp = await this.exportFn();
        // If the server signalled an error with a JSON body, the response
        // status is still 4xx so axios throws — handled in catch. Defensive
        // check here for the rare case the body slipped through as 200.
        if (isJsonBody(resp)) {
          const parsed = await readBlobAsJSON(resp.data);
          // Carry the parsed body on a real Error so describeExportError
          // still finds it at err.response.data, and stack traces work.
          const jsonError = new Error("export returned a JSON error body");
          jsonError.response = { data: parsed };
          throw jsonError;
        }
        downloadSpreadsheet(resp, this.exportFilename);
      } catch (err) {
        this.exportError = await describeExportError(
          err,
          msg => this.$t(msg),
          this.$t("export.failed")
        );
      } finally {
        this.exporting = false;
      }
    }
  }
};
